import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { Reveal } from '../components/motion.tsx';
import { Button, Callout, Chip, Dialog, Field, PageHead, Panel, Skeleton, Switch, useToast } from '../components/ui.tsx';
import { ApiError, get, patch, post } from '../lib/api.ts';
import { relative } from '../lib/format.ts';

interface Client {
  clientId: string;
  name: string;
  redirectUris: string[];
  legacyCompat: boolean;
  firstParty: boolean;
  secretHint: string | null;
  legacyCalls30d: number;
  lastLegacyCall: string | null;
  disabledAt: string | null;
  scopes: string[];
  legacyUserShape: 'flat' | 'results';
}

export function Developer() {
  const q = useQuery({ queryKey: ['clients'], queryFn: () => get<Client[]>('/api/clients') });
  const discovery = useQuery({ queryKey: ['oidc-discovery'], queryFn: () => fetch('/oidc/.well-known/openid-configuration').then((r) => r.json() as Promise<Record<string, unknown>>) });
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState<{ clientId: string; clientSecret: string | null } | null>(null);
  const [editing, setEditing] = useState<Client | null>(null);
  const qc = useQueryClient();
  const rotate = useMutation({ mutationFn: (id: string) => post<{ clientSecret: string }>(`/api/clients/${id}/rotate`), onSuccess: (r, id) => setShown({ clientId: id, clientSecret: r.clientSecret }) });
  const legacy = (q.data ?? []).filter((c) => c.legacyCompat);
  return (
    <div>
      <PageHead
        eyebrow="Apps & sign-in"
        title={
          <>
            One identity, <em>many doors.</em>
          </>
        }
        lede="Every GX app signs people in here. New apps use OpenID Connect; older apps keep the legacy /oauth2 endpoints until they migrate, and each call they make is counted below."
        aside={
          <Button variant="primary" icon="plus" onClick={() => setOpen(true)}>
            Register app
          </Button>
        }
      />
      <Reveal className="stack">
        {legacy.some((c) => c.legacyCalls30d > 0) ? (
          <Reveal.Item>
            <Callout title={`${legacy.filter((c) => c.legacyCalls30d > 0).length} app(s) still use legacy OAuth.`}>Move them to the OIDC endpoints (authorization code + PKCE). The migration guide is in docs/04-identity-and-integrations.md.</Callout>
          </Reveal.Item>
        ) : null}
        <Reveal.Item>
          <Panel eyebrow="Registered" title="Applications">
            {q.isLoading ? (
              <Skeleton h={180} />
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>App</th>
                    <th>Client ID</th>
                    <th>Protocol</th>
                    <th className="right">Legacy calls · 30d</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {q.data?.map((c) => (
                    <tr key={c.clientId}>
                      <td>
                        <div>{c.name}</div>
                        <div className="faint mono" style={{ fontSize: 10.5 }}>
                          {c.redirectUris[0]}
                        </div>
                      </td>
                      <td className="mono">{c.clientId}</td>
                      <td>{c.legacyCompat ? <Chip tone="warn">Legacy OAuth</Chip> : <Chip tone="ok">OIDC</Chip>}</td>
                      <td className="right num">
                        {c.legacyCalls30d}
                        {c.lastLegacyCall ? <div className="faint" style={{ fontSize: 10.5 }}>{relative(c.lastLegacyCall)}</div> : null}
                      </td>
                      <td className="right">
                        <div className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                          {c.disabledAt ? <Chip tone="bad">Disabled</Chip> : null}
                          <Button size="sm" variant="ghost" onClick={() => setEditing(c)}>
                            Edit
                          </Button>
                          <Button size="sm" variant="ghost" icon="key" loading={rotate.isPending && rotate.variables === c.clientId} onClick={() => rotate.mutate(c.clientId)}>
                            Rotate secret
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
        </Reveal.Item>
        <Reveal.Item className="grid-12">
          <div className="col-7">
            <Panel eyebrow="OpenID Connect" title="Endpoints">
              <dl className="dl">
                {['issuer', 'authorization_endpoint', 'token_endpoint', 'userinfo_endpoint', 'jwks_uri', 'end_session_endpoint'].map((k) => (
                  <Fragment key={k}>
                    <dt>{k.replace(/_/g, ' ')}</dt>
                    <dd className="mono" style={{ fontSize: 12 }}>
                      {String(discovery.data?.[k] ?? '…')}
                    </dd>
                  </Fragment>
                ))}
              </dl>
            </Panel>
          </div>
          <div className="col-5">
            <Panel eyebrow="Legacy compatibility" title="Still answering">
              <ul className="list-plain mono" style={{ fontSize: 12 }}>
                <li className="mini-row">GET /oauth2/authorization</li>
                <li className="mini-row">POST /oauth2/login</li>
                <li className="mini-row">POST /oauth2/token</li>
                <li className="mini-row">GET /oauth2/user</li>
                <li className="mini-row">GET /oauth2/chooseEmployee</li>
              </ul>
              <p className="faint" style={{ fontSize: 'var(--step--2)', marginTop: 10 }}>
                Same response shapes as before. Codes are now single-use and expire in 60 seconds.
              </p>
            </Panel>
          </div>
        </Reveal.Item>
      </Reveal>
      {editing ? <EditDialog client={editing} onClose={() => setEditing(null)} /> : null}
      <RegisterDialog open={open} onClose={() => setOpen(false)} onCreated={(r) => { setShown(r); void qc.invalidateQueries({ queryKey: ['clients'] }); }} />
      <Dialog open={!!shown} onClose={() => setShown(null)} eyebrow="Shown once" title="Client credentials" footer={<Button variant="primary" onClick={() => setShown(null)}>Done</Button>}>
        <div className="stack">
          <Field label="Client ID">{(p) => <input {...p} className="input input--mono" readOnly value={shown?.clientId ?? ''} />}</Field>
          {shown?.clientSecret ? <Field label="Client secret" hint="Store it in the app’s secret manager. Hearth can’t show it again.">{(p) => <input {...p} className="input input--mono" readOnly value={shown.clientSecret ?? ''} onFocus={(e) => e.currentTarget.select()} />}</Field> : <Callout tone="info">Public client: uses PKCE, no secret.</Callout>}
        </div>
      </Dialog>
    </div>
  );
}

function RegisterDialog({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (r: { clientId: string; clientSecret: string | null }) => void }) {
  const [name, setName] = useState('');
  const [uri, setUri] = useState('https://');
  const [confidential, setConfidential] = useState(true);
  const [legacyCompat, setLegacy] = useState(false);
  const m = useMutation({
    mutationFn: () => post<{ clientId: string; clientSecret: string | null }>('/api/clients', { name, redirectUris: [uri], confidential, legacyCompat }),
    onSuccess: (r) => {
      onClose();
      onCreated(r);
    },
  });
  return (
    <Dialog open={open} onClose={onClose} eyebrow="New application" title="Register an app" footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" loading={m.isPending} onClick={() => m.mutate()}>Register</Button></>}>
      <div className="stack">
        <Field label="Name">{(p) => <input {...p} className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="GX Ticketing" />}</Field>
        <Field label="Redirect URI">{(p) => <input {...p} className="input input--mono" value={uri} onChange={(e) => setUri(e.target.value)} />}</Field>
        <div className="row row--between">
          <span>Server-side app (has a secret)</span>
          <Switch checked={confidential} onChange={setConfidential} label="Confidential client" />
        </div>
        <div className="row row--between">
          <span>Allow legacy /oauth2 endpoints</span>
          <Switch checked={legacyCompat} onChange={setLegacy} label="Legacy compatibility" />
        </div>
        {m.error ? <Callout tone="bad">{m.error instanceof ApiError ? m.error.message : 'Failed'}</Callout> : null}
      </div>
    </Dialog>
  );
}

const SCOPES = ['openid', 'profile', 'email', 'employee', 'rbac', 'offline_access'] as const;

function EditDialog({ client, onClose }: { client: Client; onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ name: client.name, redirectUris: client.redirectUris.join('\n'), scopes: client.scopes, legacyCompat: client.legacyCompat, legacyUserShape: client.legacyUserShape, firstParty: client.firstParty, disabled: !!client.disabledAt });
  const m = useMutation({
    mutationFn: () => patch(`/api/clients/${client.clientId}`, { ...f, redirectUris: f.redirectUris.split(/\s+/).filter(Boolean) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clients'] });
      onClose();
    },
  });
  const toggle = (s: string) => setF({ ...f, scopes: f.scopes.includes(s) ? f.scopes.filter((x) => x !== s) : [...f.scopes, s] });
  return (
    <Dialog
      open
      onClose={onClose}
      eyebrow={client.clientId}
      title={client.name}
      width={620}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={m.isPending} onClick={() => m.mutate()}>Save</Button>
        </>
      }
    >
      <div className="stack">
        <Field label="Name">{(p) => <input {...p} className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />}</Field>
        <Field label="Redirect URIs" hint="One per line. Add the app’s OIDC callback here when it migrates.">{(p) => <textarea {...p} className="input input--mono" rows={3} value={f.redirectUris} onChange={(e) => setF({ ...f, redirectUris: e.target.value })} />}</Field>
        <div>
          <div className="eyebrow" style={{ marginBottom: 8 }}>Scopes</div>
          <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
            {SCOPES.map((s) => (
              <button key={s} type="button" className="chip-toggle" aria-pressed={f.scopes.includes(s)} disabled={s === 'openid'} onClick={() => toggle(s)}>
                {s}
              </button>
            ))}
          </div>
          <p className="faint" style={{ fontSize: 'var(--step--2)', marginTop: 6 }}>rbac adds roles, effective permissions and offices: what erp-manager’s has_permission() used to read from the ERP database.</p>
        </div>
        <Switch checked={f.legacyCompat} onChange={(v) => setF({ ...f, legacyCompat: v })} label="Legacy /oauth2 compatibility" showLabel />
        {f.legacyCompat ? (
          <Field label="/oauth2/user shape">{(p) => <select {...p} className="select" value={f.legacyUserShape} onChange={(e) => setF({ ...f, legacyUserShape: e.target.value as 'flat' | 'results' })}><option value="flat">flat (current server)</option><option value="results">results.employee (globalxtreme/oauth2 library)</option></select>}</Field>
        ) : null}
        <Switch checked={f.firstParty} onChange={(v) => setF({ ...f, firstParty: v })} label="First-party (no consent screen)" showLabel />
        <Switch checked={f.disabled} onChange={(v) => setF({ ...f, disabled: v })} label="Disabled — refuse all sign-ins" showLabel />
        {m.error ? <Callout tone="bad">{m.error instanceof ApiError ? m.error.message : 'Failed'}</Callout> : null}
      </div>
    </Dialog>
  );
}
